import './StrikeZoneHeatmap.scss';

import { HeatmapChartDataDto } from 'types/CodegenReportTypes';

import { ReactComponent as BasePlateUp } from 'svg/BasePlateUp.svg';
import { ReactComponent as BasePlateDown } from 'svg/BasePlateDown.svg';
import { Typography } from '@trackman/web-shared-components';
import { getOrderedColorSet } from 'utilities/colorHelpers';
import { useChartDataDisplay } from 'hooks/helperHooks';
import { useCallback, useMemo } from 'react';

const getRandomValue = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};

type HeatmapData = Omit<HeatmapChartDataDto, 'chartId'>;

type Props = {
  chartData: HeatmapData | undefined;
  isLoading: boolean;
  playerType: 'Batter' | 'Pitcher';
  formatValue: (value: number | null) => string;
  scaleMin: number;
  scaleMax: number;
};

const StrikeZoneHeatmap = ({ chartData, isLoading, playerType, formatValue, scaleMin, scaleMax }: Props) => {
  const simulateChartData = useCallback(
    (isLoading: boolean) => {
      return {
        outerCorners: {
          outerTopLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          outerTopRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          outerBottomRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          outerBottomLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
        },
        innerCorners: {
          innerTopLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          innerTopRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          innerBottomLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          innerBottomRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
        },
        center: {
          centerTopLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerTopRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerBottomLeftCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerBottomRightCorner: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerLeft: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerTop: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerRight: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          centerBottom: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
          center: isLoading ? getRandomValue(scaleMin, scaleMax) : null,
        },
      };
    },
    [scaleMin, scaleMax]
  );

  const { outerCorners, innerCorners, center } = useChartDataDisplay(chartData, simulateChartData, isLoading);

  const values = useMemo(() => {
    return playerType === 'Pitcher'
      ? {
          outer: [
            outerCorners.outerTopLeftCorner,
            outerCorners.outerTopRightCorner,
            outerCorners.outerBottomLeftCorner,
            outerCorners.outerBottomRightCorner,
          ],
          inner: [
            innerCorners.innerTopLeftCorner,
            innerCorners.innerTopRightCorner,
            innerCorners.innerBottomLeftCorner,
            innerCorners.innerBottomRightCorner,
          ],
          center: [
            center.centerTopLeftCorner,
            center.centerTop,
            center.centerTopRightCorner,
            center.centerLeft,
            center.center,
            center.centerRight,
            center.centerBottomLeftCorner,
            center.centerBottom,
            center.centerBottomRightCorner,
          ],
        }
      : {
          outer: [
            outerCorners.outerTopRightCorner,
            outerCorners.outerTopLeftCorner,
            outerCorners.outerBottomRightCorner,
            outerCorners.outerBottomLeftCorner,
          ],
          inner: [
            innerCorners.innerTopRightCorner,
            innerCorners.innerTopLeftCorner,
            innerCorners.innerBottomRightCorner,
            innerCorners.innerBottomLeftCorner,
          ],
          center: [
            center.centerTopRightCorner,
            center.centerTop,
            center.centerTopLeftCorner,
            center.centerRight,
            center.center,
            center.centerLeft,
            center.centerBottomRightCorner,
            center.centerBottom,
            center.centerBottomLeftCorner,
          ],
        };
  }, [outerCorners, innerCorners, center]);

  return (
    <div className='StrikeZoneHeatmap'>
      <div className='StrikeZoneHeatmap-container'>
        <div className='chart-area'>
          {values.outer.map((value, i) => (
            <div className='rect-fourth' key={'outer-' + i}>
              <Typography className='value' component='div'>
                {formatValue(value)}
              </Typography>
            </div>
          ))}

          <div className='buffer-box'>
            {values.inner.map((value, i) => (
              <div
                className='rect-fourth'
                style={{
                  backgroundColor: value != null ? getOrderedColorSet(scaleMin, scaleMax, value).bg : undefined,
                }}
                key={'inner-' + i}
              >
                <Typography
                  className='value'
                  component='div'
                  color={value != null ? getOrderedColorSet(scaleMin, scaleMax, value).text : undefined}
                >
                  {formatValue(value)}
                </Typography>
              </div>
            ))}

            <div className='internal-box'>
              {values.center.map((value, i) => (
                <div
                  className='rect-ninth'
                  style={{
                    backgroundColor: value != null ? getOrderedColorSet(scaleMin, scaleMax, value).bg : undefined,
                  }}
                  key={'center-' + i}
                >
                  <Typography
                    className='value'
                    component='div'
                    color={value != null ? getOrderedColorSet(scaleMin, scaleMax, value).text : undefined}
                  >
                    {formatValue(value)}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
        {playerType === 'Batter' && <BasePlateDown />}
        {playerType === 'Pitcher' && <BasePlateUp />}
      </div>
    </div>
  );
};

export default StrikeZoneHeatmap;
